import React, { FC, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box, Button, IconButton, TextField, Tooltip } from '@mui/material';
import {
	DataGrid,
	GridCellParams,
	GridPaginationModel,
	GridRowParams,
	GridSortDirection,
	GridSortModel,
	useGridApiRef
} from '@mui/x-data-grid';
import type { GridColDef } from "@mui/x-data-grid/models/colDef/gridColDef";
import { ReactComponent as Activity } from 'assets/icons/activity.svg';
import { ReactComponent as Edit } from 'assets/icons/edit.svg';
import { ReactComponent as Refresh } from 'assets/icons/refresh.svg';
import dayjs from "dayjs";
import { Field, Form, Formik } from "formik";
import { ROUTES } from "routes";
import { useAppDispatch, useAppSelector } from 'store/hooks';
import {
    clearPaymentsData,
    getPaymentList,
    getPaymentListInCSV,
    getPaymentListIsLoadingSelector,
    getPaymentListSelector,
    payInRefund
} from "store/payment";
import { CustomDatePickerFormik, CustomSelectFormik } from "components/elements";
import { IPaymentRefundRow, PaymentRefundModal } from 'components/modules/PaymentRefundModal';
import toastService from "components/elements/Toastify";
import { MAX_CSV_FILE_SIZE, PAGINATION_LIMIT, PAYMENTS_PROVIDER_OPTIONS, PAYMENTS_STATUS_OPTIONS } from 'utils/constants';
import { DATE_FORMAT, SERVICES_FIELDS_NAME, SORT_DIR } from 'utils/enums';
import {
	convertCoinsToUAH,
	formatQueryString,
	getLocaleAmountString,
	parseQueryString,
} from 'utils/helpers';
import { formatDateView } from 'utils/helpers/date';
import { getTableSetting } from "utils/helpers/table-setting";
import { disablePastYear, shouldDisableMonth } from "../OtherReceiptsPage/config";
import { COLUMNS_CONFIG, downloadCSV } from './config';
import styles from "./index.module.scss";
import s from './index.module.scss';
import { useDebounce } from 'utils/hooks';
import { IPaymentsData } from 'types/payment.interface';

const getRefundErrorMessage = (error: any): string =>
    error?.errorData?.message || error?.message || error?.errorText || error?.text || 'Помилка повернення оплати';

const isRefundSuccessResponse = (response: any): boolean => {
    const status = response?.status ?? response?.data?.status ?? response?.result?.status;

    if (status === undefined || status === null) {
        return !response?.error && !response?.errorData && !response?.errorText;
    }

    const normalizedStatus = String(status).toUpperCase();

    return Number(status) === 1 || status === true || normalizedStatus === 'SUCCESS' || normalizedStatus === 'OK';
};

export const PaymentPage: FC = () => {
    const { search } = useLocation();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();

    const payments = useAppSelector(getPaymentListSelector);
    const isLoading = useAppSelector(getPaymentListIsLoadingSelector);
    const [userId, setUserId] = useState<string | undefined>(undefined);
    const [amount, setAmount] = useState<string | undefined>(undefined);
    const [cardPan, setCardPan] = useState<string | undefined>('');
    const apiRef = useGridApiRef();
    const [columnWidths, setColumnWidths] = useState({});
    const [refundPayment, setRefundPayment] = useState<IPaymentRefundRow | null>(null);
    const [isRefundModalOpen, setIsRefundModalOpen] = useState(false);
    const [isRefundSubmitting, setIsRefundSubmitting] = useState(false);

    const debouncedAmount = useDebounce(amount);
    const debouncedUserId = useDebounce(userId);
    const debouncedCardPan = useDebounce(cardPan);

    const query = useMemo(() => parseQueryString<any>(search, { parseBooleans: true, parseNumbers: true }), [search]);

    const requestBody = useMemo(
        () => ({
            offset: (query.page || 0) * (query.pageSize || 0),
            limit: query.pageSize || PAGINATION_LIMIT.BIG,
            order: {
                field: query.field || SERVICES_FIELDS_NAME.CREATED_AT,
                direction: query.sort || SORT_DIR.DSC,
            },
            options: {
                paymentId: query.searchQuery && [query.searchQuery],
                user: debouncedUserId ? {
                    id: debouncedUserId,
                } : undefined,
                amount: debouncedAmount ? {
                    eq: Number(debouncedAmount) * 100,
                } : undefined,
                cardPan: debouncedCardPan ? [debouncedCardPan] : undefined,
                provider: query.provider ? [query.provider] : undefined,
                status: query.status ? [query.status] : undefined,
                dateRange: {
                    gte: query.gte ? dayjs(query.gte).toISOString() : dayjs().startOf("month").toISOString(),
                    lte: dayjs(query.lte).toISOString(),
                },
            }
        }),
        [query, debouncedUserId, debouncedAmount, debouncedCardPan]
    );

    const handleSearchClear = () => {
        navigate(
            {
                search: formatQueryString({
                    query: '',
                }),
            },
            { replace: true }
        );
        setUserId(undefined);
        setAmount(undefined);
        setCardPan('')
    };

    useEffect(() => {
        dispatch(getPaymentList(requestBody));
    }, [requestBody, dispatch]);

    useEffect(() => {
        return () => {
            dispatch(clearPaymentsData());
        };
    }, [dispatch]);

    const handleColumnResize = (params: any) => {
        const newWidths = {
            ...columnWidths,
            [params.colDef.field]: params.width,
        };
        setColumnWidths(newWidths);
        localStorage.setItem(ROUTES.paymentStatus, JSON.stringify(newWidths));
    };

    useEffect(() => {
        setColumnWidths(getTableSetting(ROUTES.paymentStatus));
    }, [requestBody]);

    COLUMNS_CONFIG.forEach((item: GridColDef) => {
        // @ts-ignore
        if (columnWidths && columnWidths[item.field]) {
            // @ts-ignore
            item.width = columnWidths[item.field];
            item.flex = undefined;
        }
    });

    const modifiedPaymentsData = useMemo(
        () => {
            return payments?.paymentsData.map((payment) => ({
                ...payment,
                id: payment.paymentId,
                amountRaw: payment.amount,
                feeRaw: payment.fee,
                billParentFeeRaw: payment.billParentFee,
                amount: getLocaleAmountString(convertCoinsToUAH(payment.amount)),
                fee: getLocaleAmountString(convertCoinsToUAH(payment.fee)),
                paidAt: formatDateView(payment.paidAt, DATE_FORMAT.DATE_FULL),
                createdAt: formatDateView(payment.createdAt, DATE_FORMAT.DATE_FULL),
							provider: typeof payment.provider === 'string' ? payment.provider : payment.provider?.providerName
						}))
        },
        [payments]
    );

    const handleChangePage = ({ page, pageSize }: GridPaginationModel): void => {
        navigate(
            {
                search: formatQueryString({
                    ...query,
                    page,
                    pageSize,
                }),
            },
            { replace: true }
        );
    };

    const handleDownloadCSV = async () => {
        if (payments && payments.listData.totalRows > MAX_CSV_FILE_SIZE) {
            toastService.warning(`Даних має бути менше ніж ${MAX_CSV_FILE_SIZE}`)
            return;
        }
        const { payload } = await dispatch(getPaymentListInCSV({
            ...requestBody,
            options: { ...requestBody.options, inCSV: "YES" }
        })) as { payload: { csvFileData: string } };
        const dateGte = formatDateView(query.gte, DATE_FORMAT.DATE_VIEW)
        const dateLte = formatDateView(query.lte, DATE_FORMAT.DATE_VIEW)
        downloadCSV(payload.csvFileData, `data_${dateGte}-${dateLte}.csv`);
    }

    const handleChangeClientId = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = event.target;
        setUserId(value);
    };

    const handleChangeAmount = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = event.target;
        setAmount(value);
    };

    const handleChangeCardPan = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = event.target;
        setCardPan(value);
    };

    const handleSortField = (model: GridSortModel): void => {
        const { field, sort } = model[0] || {};
        navigate(
            {
                search: formatQueryString({
                    ...query,
                    field,
                    sort,
                }),
            },
            { replace: true }
        );
    };

    const handleClickEdit = (params: GridCellParams) => {
        if (params.row.externalId === null) {
            toastService.error('Запис не знайдено, billId відсутнє');
            return;
        }
        navigate(`/${ROUTES.paymentStatus}/${params.row.externalId}`);
    };

    const handleClickLogs = (params: GridCellParams) => {
        if (!params.row.paymentId) {
            toastService.error('Запис не знайдено, paymentId відсутній');
            return;
        }

        window.open(`/${ROUTES.paymentStatus}/logs/${params.row.paymentId}`, '_blank', 'noopener,noreferrer');
    };

    const handleOpenRefundModal = (params: GridCellParams) => {
        if (!params.row.paymentId) {
            toastService.error('Запис не знайдено, paymentId відсутній');
            return;
        }

        setRefundPayment({
            clientId: params.row.clientId,
            paymentId: params.row.paymentId,
            provider: params.row.provider,
            amountRaw: params.row.amountRaw,
            feeRaw: params.row.feeRaw,
            billParentFeeRaw: params.row.billParentFeeRaw,
        });
        setIsRefundModalOpen(true);
    };

    const handleCloseRefundModal = () => {
        if (isRefundSubmitting) return;

        setIsRefundModalOpen(false);
        setRefundPayment(null);
    };

    const handleSubmitRefund = async (values: { withoutBank: 'YES' | 'NO'; amount?: number }) => {
        if (!refundPayment) return;

        setIsRefundSubmitting(true);

        try {
            const response = await dispatch(payInRefund({
                clientId: refundPayment.clientId,
                paymentId: refundPayment.paymentId,
                withoutBank: values.withoutBank,
                ...(values.amount ? { amount: values.amount } : {}),
            })).unwrap();

            if (isRefundSuccessResponse(response)) {
                toastService.success('Повернення виконано успішно');
                setIsRefundModalOpen(false);
                setRefundPayment(null);
                dispatch(getPaymentList(requestBody));
            } else {
                toastService.error(getRefundErrorMessage(response));
            }
        } catch (error) {
            toastService.error(getRefundErrorMessage(error));
        } finally {
            setIsRefundSubmitting(false);
        }
    };

    const handleSearchChange = (values: any): void => {
        const { gte, lte, provider, status } = values;
        if (dayjs(lte || undefined).add(-2, 'month') >= dayjs(gte || undefined)) {
            toastService.error('Проміжок від початкової та кінцевої дати повинен бути не більше двох місяців')
            return;
        }
        navigate({
            search: formatQueryString({
                ...query,
                ...values,
                status,
                provider,
                gte: gte ? dayjs(gte).startOf('day').format() : null,
                lte: lte ? dayjs(lte).endOf('day').format() : null,
            })
        }, { replace: true })
    }

    const initialValues = {
        gte: null,
        lte: null,
        status: '',
        provider: '',
    }

		const handleRowClick = (params: GridRowParams<IPaymentsData>) => {
			const externalId = params.row.externalId;
			if (!externalId || externalId.length < 10) {
				toastService.info('Інформація у білінгу відсутня, платіж був зроблений на зовнішніх ресурсах')
			}
		}

    return (
        <>
            <Box>
                <Box>
                    <Box className={s.filter}>
                        <TextField
                            className={s.filter__text}
                            value={userId ?? ''}
                            placeholder="Пошук по id користувача"
                            onChange={handleChangeClientId}
                        />
                        <TextField
                            className={s.filter__text}
                            value={amount ?? ''}
                            type="number"
                            placeholder="Пошук по сумі оплати"
                            onChange={handleChangeAmount}
                        />
                        <TextField
                            className={s.filter__text}
                            value={cardPan}
                            placeholder="Пошук по PANy"
                            onChange={handleChangeCardPan}
                        />
                        <Button variant="contained" onClick={handleDownloadCSV}>Завантажити CSV</Button>
                    </Box>
                    <Formik initialValues={initialValues} onSubmit={handleSearchChange}>
                        <Form className={s.filter}>
                            <Box className={s.date__filters}>
                                <Field
                                    name="gte"
                                    views={['year', 'month', 'day']}
                                    format={DATE_FORMAT.DAY_MONTH_NAME_YEAR}
                                    shouldDisableMonth={shouldDisableMonth}
                                    shouldDisableYear={disablePastYear}
                                    className={styles.date__input}
                                    component={CustomDatePickerFormik}
                                />
                                <span>-</span>
                                <Field
                                    name="lte"
                                    views={['year', 'month', 'day']}
                                    format={DATE_FORMAT.DAY_MONTH_NAME_YEAR}
                                    shouldDisableMonth={shouldDisableMonth}
                                    shouldDisableYear={disablePastYear}
                                    className={styles.date__input}
                                    component={CustomDatePickerFormik}
                                />
                            </Box>
                            <Field
                                name="status"
                                placeholder="Статус"
                                options={PAYMENTS_STATUS_OPTIONS}
                                component={CustomSelectFormik}
                            />
                            <Field
                                name="provider"
                                placeholder="Провайдер"
                                options={PAYMENTS_PROVIDER_OPTIONS}
                                component={CustomSelectFormik}
                            />
                            <Button variant="contained" type="submit">Застосувати</Button>
                            <Button variant="outlined" type="reset" onClick={handleSearchClear}>Очистити</Button>
                        </Form>
                    </Formik>
                </Box>
            </Box>
            <Box
                className={s.tableWrapper}
                sx={{
                    '& .negative': {
                        color: 'text.secondary',
                    },
                    '& .positive': {
                        color: 'success.main',
                    },
                }}
            >
                <DataGrid
                    disableRowSelectionOnClick
                    disableColumnFilter
                    onColumnResize={handleColumnResize}
                    apiRef={apiRef}
										onRowClick={(params) => handleRowClick(params)}
                    rows={modifiedPaymentsData || []}
                    columns={[
                        ...COLUMNS_CONFIG,
                        {
                            field: 'action',
                            headerName: 'Дія',
                            sortable: false,
                            align: 'center',
                            width: 120,
                            renderCell: (params) => {
                                const hasPaymentId = !!params.row.paymentId
                                return hasPaymentId ? (
                                    <Box display="flex" justifyContent="center" alignItems="center" gap={0.5} width="100%">
                                        <Tooltip title="Логи" placement="top">
                                            <IconButton
                                                onClick={(event) => {
                                                    event.stopPropagation();
                                                    handleClickLogs(params);
                                                }}
                                                color="primary"
                                                aria-label="Логи"
                                            >
                                                <Activity />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Повернути оплату" placement="top">
                                            <IconButton
                                                onClick={(event) => {
                                                    event.stopPropagation();
                                                    handleOpenRefundModal(params);
                                                }}
                                                color="primary"
                                                aria-label="Повернути оплату"
                                            >
                                                <Refresh />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                ) : null;
                            }
                        },
                        {
                            field: 'edit',
                            headerName: ' ',
                            sortable: false,
                            align: 'center',
                            width: 60,
                            renderCell: (params) => {
															const isHaveExternalId = params.row.externalId
															return isHaveExternalId && isHaveExternalId.length > 10 ? (
																<IconButton onClick={() => handleClickEdit(params)} color="primary" aria-label="Edit">
																<Edit/>
															</IconButton>
															) : null;
														}
                        },
                    ]}
                    rowCount={payments?.listData.totalRows || 0}
                    loading={isLoading}
                    initialState={{
                        pagination: {
                            paginationModel: {
                                page: query.page || 0,
                                pageSize: query.pageSize || PAGINATION_LIMIT.BIG
                            },
                        },
                        sorting: {
                            sortModel: [{
                                field: query.field || SERVICES_FIELDS_NAME.ID,
                                sort: query.sort as GridSortDirection
                            }],
                        },
                    }}
                    slotProps={{
                        pagination: {
                            labelRowsPerPage: 'Показувати :',
                            page: query.page || 0,
                            rowsPerPage: query.pageSize || PAGINATION_LIMIT.BIG,
                        },
                    }}
                    paginationMode="server"
                    sortingMode="server"
                    onSortModelChange={handleSortField}
                    onPaginationModelChange={handleChangePage}
                    className={s.table}
                    pageSizeOptions={[PAGINATION_LIMIT.SMALL, PAGINATION_LIMIT.MIDDLE, PAGINATION_LIMIT.BIG]}
                />
            </Box>
            <PaymentRefundModal
                open={isRefundModalOpen}
                payment={refundPayment}
                isSubmitting={isRefundSubmitting}
                onClose={handleCloseRefundModal}
                onSubmit={handleSubmitRefund}
            />
        </>
    );
};
